import { useCallback, useEffect, useState } from "react"
import Chart from "react-apexcharts"
import type { ApexOptions } from "apexcharts"
import api from "../../lib/api"
import { getErrorMessage } from "../../lib/errors"
import ModuleLayout from "../../components/admin/ModuleLayout"
import { LoadState } from "@/components/ui/LoadState"

type MisalignedStudent = {
  seedbedId: string
  seedbedName: string
  userId: string
  studentName: string | null
  expectedProgressPercent: number
  actualProgressPercent: number
}

type PendingSubmission = {
  submissionId: string
  studentName: string | null
  projectTitle: string
  submittedAt: string
  daysWaiting: number
}

type LessonDropoff = {
  courseId: string
  courseTitle: string
  lessonId: string
  lessonTitle: string
  reachedCount: number
  previousReachedCount: number
  dropoffPercent: number
}

type RepeatedQuizFailure = {
  sectionQuizId: string
  quizTitle: string | null
  affectedStudentCount: number
}

type NearGraduate = {
  userId: string
  studentName: string | null
  courseId: string
  progressPercent: number
}

type InstructorStats = {
  misalignedStudents: MisalignedStudent[]
  seedbedsWithoutCalendarCount: number
  pendingSubmissionsQueue: PendingSubmission[]
  lessonDropoff: LessonDropoff[]
  repeatedQuizFailures: RepeatedQuizFailure[]
  nearGraduates: NearGraduate[]
}

export default function InstructorStatsPage() {
  const [data, setData] = useState<InstructorStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get("/academy/instructor-stats")
      setData(res.data.data ?? res.data)
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const worstDropoff = [...(data?.lessonDropoff ?? [])]
    .sort((a, b) => b.dropoffPercent - a.dropoffPercent)
    .slice(0, 10)

  const chartOptions: ApexOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
    dataLabels: { enabled: true, formatter: (val) => `${val}%` },
    xaxis: {
      categories: worstDropoff.map((d) => d.lessonTitle),
      max: 100,
    },
    colors: ["#f04438"],
  }
  const chartSeries = [{ name: "Caída (%)", data: worstDropoff.map((d) => d.dropoffPercent) }]

  return (
    <ModuleLayout
      breadcrumbs={[{ label: "SateLab", to: "/" }, "Docentes", "Estadísticas"]}
      title="Estadísticas de acción"
      subtitle="Qué necesita tu atención esta semana — no inventario"
      tabs={[
        { label: "Hub", to: "/instructor/hub" },
        { label: "Entregas", to: "/instructor/submissions" },
        { label: "Estudiantes", to: "/instructor/students" },
        { label: "Estadísticas", to: "/instructor/stats", active: true },
      ]}
      metrics={[
        { label: "Alumnos desalineados", value: String(data?.misalignedStudents.length ?? "—"), tone: "warning" },
        { label: "Entregas pendientes", value: String(data?.pendingSubmissionsQueue.length ?? "—"), tone: "warning" },
        { label: "Casi-graduados sin certificar", value: String(data?.nearGraduates.length ?? "—"), tone: "good" },
        { label: "Semilleros sin calendario", value: String(data?.seedbedsWithoutCalendarCount ?? "—") },
      ]}
    >
      {loading && <LoadState />}
      {error && <p role="alert" aria-live="assertive" className="text-error-600 dark:text-error-400">{error}</p>}
      {!loading && !error && data && (
        <div className="space-y-6">
          <section className="rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
              Alumnos desalineados con el calendario del semillero
            </h3>
            {data.misalignedStudents.length === 0 ? (
              <p className="text-sm text-gray-500">Nadie está por debajo del progreso esperado.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {data.misalignedStudents.map((m) => (
                  <li key={`${m.seedbedId}-${m.userId}`} className="flex justify-between">
                    <span>{m.studentName ?? "Sin nombre"} — {m.seedbedName}</span>
                    <span className="text-gray-500">
                      {m.actualProgressPercent}% real vs {m.expectedProgressPercent}% esperado
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
              Cola de entregas por calificar (más antiguas primero)
            </h3>
            {data.pendingSubmissionsQueue.length === 0 ? (
              <p className="text-sm text-gray-500">No hay entregas pendientes.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {data.pendingSubmissionsQueue.slice(0, 20).map((s) => (
                  <li key={s.submissionId} className="flex justify-between">
                    <span>{s.studentName ?? "Sin nombre"} — {s.projectTitle}</span>
                    <span className="text-gray-500">{s.daysWaiting}d esperando</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
              Punto de fuga por lección (mayor caída primero)
            </h3>
            {worstDropoff.length === 0 ? (
              <p className="text-sm text-gray-500">Sin datos suficientes todavía.</p>
            ) : (
              <div className="max-w-full overflow-x-auto custom-scrollbar">
                <div className="min-w-[600px]">
                  <Chart options={chartOptions} series={chartSeries} type="bar" height={Math.max(180, worstDropoff.length * 32)} />
                </div>
              </div>
            )}
          </section>

          <section className="rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
              Quizzes con reprobación repetida
            </h3>
            {data.repeatedQuizFailures.length === 0 ? (
              <p className="text-sm text-gray-500">Ningún quiz tiene alumnos con 2+ intentos fallidos.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {data.repeatedQuizFailures.map((q) => (
                  <li key={q.sectionQuizId} className="flex justify-between">
                    <span>{q.quizTitle ?? "Quiz sin título"}</span>
                    <span className="text-gray-500">{q.affectedStudentCount} alumno(s) afectado(s)</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
              Casi-graduados sin certificar (≥80% de progreso)
            </h3>
            {data.nearGraduates.length === 0 ? (
              <p className="text-sm text-gray-500">Nadie está en este rango sin certificado.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {data.nearGraduates.map((g) => (
                  <li key={`${g.courseId}-${g.userId}`} className="flex justify-between">
                    <span>{g.studentName ?? "Sin nombre"}</span>
                    <span className="text-gray-500">{g.progressPercent}% de progreso</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </ModuleLayout>
  )
}
